import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, Loader2, XCircle } from "lucide-react";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (!cancelled) setStatus("paid");
          return;
        }
      } catch {}
      attempts.current += 1;
      if (attempts.current < 10) {
        setTimeout(poll, 2000);
      } else if (!cancelled) {
        setStatus("failed");
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#050505] text-white font-body flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="neon-trace bg-[#0A0A0A] p-10 w-full max-w-md text-center"
      >
        <p className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-[#CCFF00] mb-8">HITMAX — VIP</p>
        {status === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="w-8 h-8 animate-spin text-[#CCFF00] mx-auto mb-6" />
            <p className="text-zinc-300">{t.payment.checking}</p>
          </div>
        )}
        {status === "paid" && (
          <div data-testid="payment-success">
            <span className="w-14 h-14 rounded-full bg-[#CCFF00]/10 border border-[#CCFF00]/40 flex items-center justify-center mx-auto mb-6">
              <Check className="w-7 h-7 text-[#CCFF00]" />
            </span>
            <h1 className="font-display font-black tracking-tight text-2xl mb-3">{t.payment.paid}</h1>
            <p className="text-sm text-zinc-400 mb-8">{t.payment.paidSub}</p>
            <a href="/" data-testid="payment-home-link" className="font-mono2 text-xs tracking-widest uppercase text-[#CCFF00] hover:underline">
              {t.payment.home}
            </a>
          </div>
        )}
        {status === "failed" && (
          <div data-testid="payment-failed">
            <XCircle className="w-8 h-8 text-red-400 mx-auto mb-6" />
            <p className="text-zinc-300 mb-8">{t.payment.failed}</p>
            <a href="/" data-testid="payment-home-link" className="font-mono2 text-xs tracking-widest uppercase text-[#CCFF00] hover:underline">
              {t.payment.home}
            </a>
          </div>
        )}
      </motion.div>
    </div>
  );
}
